"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ANALYTICS_QUERY_KEYS = exports.REPORT_BUILDER_PATH = exports.DROP_OFF_PATH = exports.SCOREBOARD_PATH = exports.PROFICIENCY_PATH = exports.recordWithdrawalSchema = exports.updateSavedReportSchema = exports.saveReportSchema = exports.reportScheduleSchema = exports.savedReportFiltersSchema = exports.SCHEDULE_HOUR = exports.WEEKDAY_LABELS = exports.DEFAULT_SCHEDULE_WEEKDAY = exports.SCHEDULE_FREQUENCY_LABELS = exports.SCHEDULE_FREQUENCIES = exports.reportExportQuerySchema = exports.EXPORT_FORMATS = exports.reportTableQuerySchema = exports.REPORT_COLUMN_KINDS = exports.DROP_OFF_KINDS = exports.SOURCE_MIX_ROWS = exports.BENCHMARK_TRAILING_MONTHS = exports.BENCHMARK_MIN_FILLS = exports.analyticsDrillQuerySchema = exports.analyticsQuerySchema = exports.ROLE_FAMILY_UNCLASSIFIED = exports.ANALYTICS_REPORT_INFO = exports.ANALYTICS_REPORTS = exports.ANALYTICS_AUDIENCES = void 0;
exports.analyticsReportInfo = analyticsReportInfo;
exports.isScheduleDue = isScheduleDue;
exports.canRecordWithdrawal = canRecordWithdrawal;
exports.courtSegments = courtSegments;
exports.rateOf = rateOf;
exports.fillIndex = fillIndex;
exports.severityIndexOf = severityIndexOf;
const zod_1 = require("zod");
const decisions_1 = require("./decisions");
const state_machines_1 = require("./state-machines");
const reports_1 = require("./reports");
const schemas_1 = require("./schemas");
/**
 * S29 — RPT-03 (recruiter proficiency), RPT-04 (department scoreboard and panelist activity),
 * RPT-05 (drop-off and source effectiveness) and RPT-06 (the report builder: saved reports,
 * CSV / XLSX export, scheduled delivery). Every figure is computed from the event log on read
 * (#10) and opens the records behind it (F17.02).
 */
// --- Who is looking -----------------------------------------------------------------------------
/**
 * §L23: "Reports — all staff" is the HR Lead's; "Reports — own" narrows a recruiter to their own
 * records and a hiring manager to their own departments (DECISIONS-LOG S29–S32 Q3).
 */
exports.ANALYTICS_AUDIENCES = ['all', 'recruiter', 'manager'];
// --- The reports (RPT-06's library) --------------------------------------------------------------
exports.ANALYTICS_REPORTS = [
    'recruiter_proficiency',
    'department_scoreboard',
    'panelist_activity',
    'drop_off',
    'source_effectiveness',
    'funnel',
    'open_requisitions',
    'time_to_fill_trend',
    'offer_analytics',
];
exports.ANALYTICS_REPORT_INFO = [
    { key: 'recruiter_proficiency', code: 'RPT-03a', name: 'Recruiter proficiency', description: 'A profile per recruiter — never a ranking.', audiences: ['all', 'recruiter'], screen: '/reports/proficiency' },
    { key: 'department_scoreboard', code: 'RPT-04a', name: 'Department scoreboard', description: 'Approval, decision and scorecard delays, no-shows, reschedules and cancellations per department.', audiences: ['all', 'manager'], screen: '/reports/scoreboard' },
    { key: 'panelist_activity', code: 'RPT-04b', name: 'Panelist activity', description: 'Interviews, scorecards, lag, no-shows and the severity index per panelist.', audiences: ['all', 'manager'], screen: '/reports/scoreboard' },
    { key: 'drop_off', code: 'RPT-05a', name: 'Drop-off analysis', description: 'Where candidates leave and why: rejections, withdrawals and no-shows by stage.', audiences: ['all', 'recruiter', 'manager'], screen: '/reports/drop-off' },
    { key: 'source_effectiveness', code: 'RPT-05b', name: 'Source effectiveness', description: 'Applications, qualified, interviewed and hired per source, with spend and cost per hire.', audiences: ['all', 'recruiter', 'manager'], screen: '/reports/drop-off' },
    { key: 'funnel', code: 'RPT-01b', name: 'Hiring funnel', description: 'Entered, advanced, rejected and withdrew per stage for the period.', audiences: ['all', 'recruiter', 'manager'], screen: null },
    { key: 'open_requisitions', code: 'RPT-01a', name: 'Open requisition aging', description: 'Every open requisition with days open, elapsed and active.', audiences: ['all', 'recruiter', 'manager'], screen: null },
    { key: 'time_to_fill_trend', code: 'RPT-06a', name: 'Time-to-fill trend', description: 'Median time to fill by month, split by role family and venture, elapsed and active.', audiences: ['all', 'recruiter', 'manager'], screen: null },
    { key: 'offer_analytics', code: 'RPT-06b', name: 'Offer analytics', description: 'Offers made, accepted, declined by reason, expired, no-shows, negotiation and offer-to-join.', audiences: ['all', 'recruiter', 'manager'], screen: null },
];
function analyticsReportInfo(key) {
    const found = exports.ANALYTICS_REPORT_INFO.find((info) => info.key === key);
    if (!found)
        throw new Error(`No report ${key}`);
    return found;
}
// --- Filters (F17.03) ----------------------------------------------------------------------------
exports.ROLE_FAMILY_UNCLASSIFIED = 'Unclassified';
exports.analyticsQuerySchema = reports_1.commandCenterQuerySchema.extend({
    roleFamily: zod_1.z.string().trim().min(1).max(80).optional(),
    jobId: schemas_1.uuidSchema.optional(),
    source: zod_1.z.string().trim().min(1).max(80).optional(),
});
exports.analyticsDrillQuerySchema = exports.analyticsQuerySchema.extend({
    report: zod_1.z.enum(exports.ANALYTICS_REPORTS),
    /** Which figure: "<userId>:lag", "<departmentId>:approval", "screening:rejected:SCR-SKILLS"… */
    key: zod_1.z.string().trim().max(160).default(''),
});
/** Q1: a family needs this many fills in the trailing 12 months before it is a benchmark. */
exports.BENCHMARK_MIN_FILLS = 3;
exports.BENCHMARK_TRAILING_MONTHS = 12;
/** How many sources a card's mix names. */
exports.SOURCE_MIX_ROWS = 4;
// --- RPT-05: drop-off and source effectiveness ---------------------------------------------------
exports.DROP_OFF_KINDS = ['rejected', 'withdrawn', 'no_show'];
// --- The builder's table (RPT-06) ----------------------------------------------------------------
exports.REPORT_COLUMN_KINDS = ['text', 'count', 'percent', 'days', 'number', 'money'];
exports.reportTableQuerySchema = exports.analyticsQuerySchema.extend({
    report: zod_1.z.enum(exports.ANALYTICS_REPORTS),
    /** Comma-separated column keys, in order; empty = every column. */
    columns: zod_1.z.string().trim().max(1000).optional(),
});
exports.EXPORT_FORMATS = ['csv', 'xlsx'];
exports.reportExportQuerySchema = exports.reportTableQuerySchema.extend({
    format: zod_1.z.enum(exports.EXPORT_FORMATS).default('xlsx'),
});
// --- Saved and scheduled reports (F17.05, F17.06) ------------------------------------------------
exports.SCHEDULE_FREQUENCIES = ['daily', 'weekly', 'monthly'];
exports.SCHEDULE_FREQUENCY_LABELS = {
    daily: 'Every day',
    weekly: 'Every week',
    monthly: 'On the 1st of every month',
};
/** ISO weekday, 1 = Monday … 7 = Sunday. The default is Sunday, the first working day here. */
exports.DEFAULT_SCHEDULE_WEEKDAY = 7;
exports.WEEKDAY_LABELS = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' };
/** Q9: scheduled reports go at 8am Bangladesh time, with the morning emails. */
exports.SCHEDULE_HOUR = 8;
exports.savedReportFiltersSchema = exports.analyticsQuerySchema;
exports.reportScheduleSchema = zod_1.z
    .object({
    frequency: zod_1.z.enum(exports.SCHEDULE_FREQUENCIES),
    weekday: zod_1.z.number().int().min(1).max(7).nullable().default(null),
})
    .refine((value) => value.frequency !== 'weekly' || value.weekday !== null, { message: 'Pick the day of the week.', path: ['weekday'] });
exports.saveReportSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Give the report a name of at least 2 characters.').max(120),
    report: zod_1.z.enum(exports.ANALYTICS_REPORTS),
    filters: exports.savedReportFiltersSchema,
    columns: zod_1.z.array(zod_1.z.string().trim().min(1).max(60)).max(60).default([]),
    isShared: zod_1.z.boolean().default(false),
    schedule: exports.reportScheduleSchema.nullable().default(null),
});
exports.updateSavedReportSchema = exports.saveReportSchema.partial().extend({ version: zod_1.z.number().int().min(1) });
/** Whether a schedule is due on this Bangladesh calendar day (`weekday` ISO 1–7). */
function isScheduleDue(schedule, day) {
    if (schedule.frequency === 'daily')
        return true;
    if (schedule.frequency === 'weekly')
        return schedule.weekday === day.weekday;
    return day.dayOfMonth === 1;
}
// --- Record a withdrawal (Q4) --------------------------------------------------------------------
exports.recordWithdrawalSchema = zod_1.z.object({
    reasonCode: zod_1.z.string().trim().min(1, 'Pick the reason the candidate gave.'),
    note: zod_1.z.string().trim().max(2000).optional(),
    version: zod_1.z.number().int().min(1),
});
/** Where "Record withdrawal" is offered: any open stage but an offer's, whose answer is recorded on the offer (S23). */
function canRecordWithdrawal(stage) {
    return !state_machines_1.APPLICATION_MACHINE.terminal.some((terminal) => terminal === stage) && stage !== 'offer' && stage !== 'hired_pending_join';
}
// --- Paths and keys ------------------------------------------------------------------------------
exports.PROFICIENCY_PATH = '/reports/proficiency';
exports.SCOREBOARD_PATH = '/reports/scoreboard';
exports.DROP_OFF_PATH = '/reports/drop-off';
exports.REPORT_BUILDER_PATH = '/reports/builder';
exports.ANALYTICS_QUERY_KEYS = [['proficiency'], ['scoreboard'], ['drop-off'], ['report-table'], ['analytics-drill']];
/**
 * Q2: an application's life cut into the stretches each party held the ball, clipped to
 * [windowFrom, windowTo). `start` is when the application began (its court is the first
 * change's, else the recruiter's — every application starts on the recruiter's desk);
 * `end` is when it closed, or now.
 */
function courtSegments(changes, start, end, windowFrom, windowTo) {
    const sorted = [...changes].filter((change) => change.at <= end).sort((a, b) => a.at - b.at);
    const points = [];
    const first = sorted[0];
    if (!first || first.at > start)
        points.push({ at: start, court: 'recruiter' });
    for (const change of sorted)
        points.push({ at: Math.max(change.at, start), court: change.court });
    const segments = [];
    for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        if (!point)
            continue;
        const next = points[index + 1];
        const from = Math.max(point.at, windowFrom);
        const to = Math.min(next ? next.at : end, windowTo, end);
        if (to > from)
            segments.push({ from, to, court: point.court });
    }
    return segments;
}
/** The share as a fraction, or null when there is nothing to divide by. */
function rateOf(numerator, denominator) {
    return denominator === 0 ? null : numerator / denominator;
}
/** Q1: each fill's active time against its family's benchmark; the recruiter's index is the median. */
function fillIndex(activeMinutes, benchmarkMinutes) {
    if (benchmarkMinutes === null || benchmarkMinutes <= 0)
        return null;
    return activeMinutes / benchmarkMinutes;
}
/**
 * §L16: a panelist's mean ÷ everyone's, two decimals — null below the 10-scorecard minimum, or
 * with no global mean (RPT-04b, master plan S29 QA 4).
 */
function severityIndexOf(panelistMean, globalMean, scorecards) {
    if (globalMean === null || globalMean <= 0 || scorecards < decisions_1.SEVERITY_MIN_SCORECARDS)
        return null;
    return Math.round((panelistMean / globalMean) * 100) / 100;
}
//# sourceMappingURL=analytics.js.map